import { Button, Typography } from "@lyfegen-platform/react-common";
import { Box, Link, makeStyles, Slide, Snackbar, Theme } from "@material-ui/core";
import { TransitionProps } from "@material-ui/core/transitions";
import { Warning } from "@material-ui/icons";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { bannerState } from "../store/layout";
import { cyrb53 } from "../utils/hash";
import { userPersonalInfoState } from "../store/user";
import SpecificErrorPage from "./SpecificErrorPage";
import LogToSentry, { getNetworkErrorStatusCode, SentryErrorLevels } from "./SentryLog";
import { NetworkError } from "@apollo/client/errors";

interface BannerProps {
	className?: string;
	style?: React.CSSProperties;
}

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		width: "100%",
		backgroundColor: "white",
		padding: theme.spacing(1),
		boxShadow: theme.shadows[1],
	},
}));

function SlideTransition(props: TransitionProps) {
	return <Slide {...props} direction="right" timeout={500} />;
}

const Banner: React.FC<BannerProps> = () => {
	const resetBanner = useResetRecoilState(bannerState);
	const error = useRecoilValue(bannerState);
	const classes = useStyles({});
	const { t } = useTranslation();
	const statusCodes = [403, 502, 503, 504, 524];
	const activeUser = useRecoilValue(userPersonalInfoState);
	const errorLog = error?.error;

	if (statusCodes.some((code) => code === (error.error as any)?.statusCode)) {
		console.error(error);
		const statusCode = (error.error as any)?.statusCode || getNetworkErrorStatusCode(error.error as NetworkError);

		return (
			<>
				<MaintenancePage statusCode={statusCode.toString()} />
				<LogToSentry
					level={SentryErrorLevels.Warning}
					errorLog={errorLog}
					statusCode={statusCode}
					userId={cyrb53(activeUser.id).toString()}
					operation={error.operation}
					variables={error.variables}
				/>
			</>
		);
	}

	return (
		<>
			{errorLog && (
				<LogToSentry
					level={SentryErrorLevels.Error}
					errorLog={errorLog}
					userId={cyrb53(activeUser.id).toString()}
					operation={error.operation}
					variables={error.variables}
				/>
			)}
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				style={{ marginTop: "2.3rem", width: "100%" }}
				open={error.isError}
				ContentProps={{ className: classes.root }}
				TransitionComponent={SlideTransition}
				key={error.message}
				message={
					<Box display="flex" pl={22}>
						<Box pr={5}>
							<Warning color="error" />
						</Box>
						{error.message ? (
							<Typography type="fieldText" color="textPrimary">
								{error.message}
							</Typography>
						) : (
							<Typography type="fieldText" color="textPrimary">
								{t("global-error-1")} (
								<Link color="primary" href="mailto:support@lyfegen.com">
									support@lyfegen.com
								</Link>
								) {t("global-error-2")}
							</Typography>
						)}
					</Box>
				}
				action={
					<Box display="flex" pr={20}>
						{error.action ? (
							error.action
						) : (
							<Button color="primary" onClick={resetBanner}>
								{t("dismiss")}
							</Button>
						)}
					</Box>
				}
			/>
		</>
	);
};

export default Banner;

const MaintenancePage = (props: { statusCode?: string }) => {
	useEffect(() => {
		document.body.style.overflow = "hidden";

		return () => {
			document.body.style.overflow = "none";
		};
	}, []);

	return (
		<div
			style={{
				position: "fixed",
				top: 0,
				bottom: 0,
				left: 0,
				right: 0,
				zIndex: 1202,
				backgroundImage: "linear-gradient(to bottom, #fbf8f5 0%, #ffffff 100%)",
			}}
		>
			<SpecificErrorPage statusCode={props.statusCode} />
		</div>
	);
};
